import "./FacilityListTableComponent.scss";

import React, {useCallback} from 'react';
import {ITableColumn} from '../../../shared/models/table.model';
import {IFacility} from "../../../shared/models/facility.model";
import SwitchComponent from "../../../shared/components/core/switch/switchComponent";
import {CommonService} from "../../../shared/services";
import {APIConfig, ImageConfig} from "../../../constants";
import {IAPIResponseType} from "../../../shared/models/api.model";
import ChipComponent from "../../../shared/components/chip/ChipComponent";
import {Link} from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TableWrapperV2Component
    from "../../../shared/components/table-components/table-wrapper-v2/TableWrapperV2Component";
import TooltipComponent from "../../../shared/components/tooltip/tooltipComponent";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import commonService from "../../../shared/services/common.service";
import {getFacilityListLite} from "../../../store/actions/staticData.action";
import {ITimelineItem} from "../../../shared/models/timeline.model";

const MODULE_NAME = "FacilityList"

interface FacilityListTableComponentProps {
    filterData?: {};
}

const FACILITY_DETAILS = 'facility_details';
const PRIMARY_CONTACT = 'primary_contact';
const PROVIDER_OR_PHYSICIAN = 'Provider_or_Physician';
const ADMIN_DETAILS = "admin_details";

const STEPS: ITimelineItem[] = [
    {
        id: FACILITY_DETAILS,
        title: "Facility Details",
    },
    {
        id: ADMIN_DETAILS,
        title: "Admin Details",
    },
    {
        id: PRIMARY_CONTACT,
        title: "Primary Contact",
    },
    {
        id: PROVIDER_OR_PHYSICIAN,
        title: "Provider/Physician",
    },
];


const FacilityListTableComponent = (props: FacilityListTableComponentProps) => {

    const {filterData} = props;
    const {currentUser} = useSelector((state: IRootReducerState) => state.account);
    const dispatch = useDispatch();

    const columns: ITableColumn[] = [
        {
            id: 'name',
            label: 'Facility Name',
            isDefaultColumn: true,
            fixed: "left",
            width: "250",
            content: (item: IFacility) => (
                <>
                    {
                        (item?.name).length > 23 ?
                            <TooltipComponent
                                tooltip={CommonService.capitalizeFirstLetterOfEachWord(item?.name)}
                                placement={"top"}
                                arrow={true}
                            >
                                <div className={"ellipses-for-table-data"}>
                                    {CommonService.capitalizeFirstLetterOfEachWord(item?.name)}
                                </div>
                            </TooltipComponent> :
                            <>
                                {CommonService.capitalizeFirstLetterOfEachWord(item?.name)}
                            </>
                    }
                </>
            )
        },
        {
            id: 'type', label: 'Facility Type', isDefaultColumn: true,
            content: (item: IFacility) => (
                <>
                    {
                        item?.type && item?.type?.length > 23 ?
                            <TooltipComponent
                                tooltip={CommonService.capitalizeFirstLetterOfEachWord(item?.type)}
                                placement={"top"}
                                arrow={true}
                            >
                                <div className={"ellipses-for-table-data"}>
                                    {CommonService.capitalizeFirstLetterOfEachWord(item?.type)}
                                </div>
                            </TooltipComponent> :
                            <>
                                {CommonService.capitalizeFirstLetterOfEachWord(item?.type || '-')}
                            </>
                    }
                </>
            )
        },
        {
            id: 'createdAt', label: 'Added Date & Time', isDefaultColumn: false, align: 'center', width: 180,
            content: (item: IFacility) => (
                <>
                    {
                        item?.createdAt ?
                            <>
                                {commonService.formatDate(item?.createdAt)}
                                &nbsp;|&nbsp;
                                {commonService.formatTime(item?.createdAt)} <br/>
                            </> : <> - </>
                    }
                </>
            )
        },
        {
            id: 'onBoardedBy', label: 'Added by', isDefaultColumn: false, align: 'center',
            content: (item: IFacility) => (
                <>
                    {item?.createdByDetails ? <>{commonService.capitalizeFirstLetterOfEachWord(item?.createdByDetails?.firstName)} {commonService.capitalizeFirstLetterOfEachWord(item?.createdByDetails?.lastName)}</> : '-'}
                </>
            )
        },

        {
            id: 'city', label: 'City', isDefaultColumn: false, align: 'center',
            content: (item: IFacility) => (
                <>
                    {item?.addressDetails ? <>{item?.addressDetails?.city} </> : '-'}
                </>
            )
        },

        {
            id: 'state', label: 'State', isDefaultColumn: false, align: 'center',
            content: (item: IFacility) => (
                <>
                    {item?.addressDetails ? <>{item?.addressDetails?.state} </> : '-'}
                </>
            )
        },

        {
            id: 'status', label: 'Facility Status', isDefaultColumn: true,
            align: 'center', width: '150',
            content: (item: IFacility) => (
                <>
                    <ChipComponent className={`min-width-60 ${item?.statusObj?.title}`}
                                   label={CommonService.capitalizeFirstLetter(item?.statusObj?.title)}
                                   variant={'outlined'}/>
                </>
            )
        },

        {
            id: 'isActive',
            label: commonService.checkAccess('Facility',
                'facilityToggle', currentUser?.userAccessModules) ? 'Active/Inactive' : "",
            align: 'center',
            width: commonService.checkAccess('Facility',
                'facilityToggle', currentUser?.userAccessModules) ? "150" : "10",
            isDefaultColumn: true,
            content: (item: IFacility, index: any) => (
                <React.Fragment>
                    {
                        commonService.checkAccess('Facility',
                            'facilityToggle', currentUser?.userAccessModules) ?
                            <SwitchComponent
                                size={"small"}
                                disabled={(item?.status === 'draft')}
                                checked={item?.isActive}
                                onUpdate={() => handleFacilityToggle(item)}/> :
                            <></>
                    }
                </React.Fragment>
            )
        },

        {
            id: 'actions',
            label: '',
            fixed: "right",
            isDefaultColumn: true,
            hideInColumnPreferences: true,
            align: 'center',
            width: "100",
            content: (item: IFacility, index: any) => (
                <React.Fragment>
                    <div className="table-actions" id={`view_facility_` + index}>
                        {item?.status === "draft" ? <>
                                <Link
                                    to={CommonService._routeConfig.FacilityAddRoute() + "?lastCompletedStep=" + (item?.lastCompletedStep ? item?.lastCompletedStep + 1 : 0) + `&facilityId=${item?.id}&step=${STEPS[item?.lastCompletedStep ? (item?.lastCompletedStep + 1) : 1]?.id}`}>
                                    <div className="table-action">
                                        <ChevronRightIcon/>
                                    </div>
                                </Link>
                            </> :
                            <Link to={CommonService._routeConfig.FacilityDetailsLocationRoute(item?.id)}>
                                <div className="table-action">
                                    <ChevronRightIcon/>
                                </div>
                            </Link>
                        }
                    </div>
                </React.Fragment>
            )
        },
    ];

    const onToggleFacility = useCallback((item: any) => {
        CommonService._facility.toggleActiveStatus(item.id, {})
            .then((response: IAPIResponseType<any>) => {
                CommonService._communications.TableWrapperRefreshSubject.next({
                    moduleName: MODULE_NAME
                });
                dispatch(getFacilityListLite());
                CommonService._alert.showToast(response?.message || `Facility has been  ${item?.isActive ? "deactivated" : "activated"}`, "success");
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error?.error || `Error while ${item?.isActive ? "deactivating" : "activating"} the location`, "error");
            });
    }, [dispatch]);

    const handleFacilityToggle = useCallback((item: any) => {
        CommonService.openConfirmationDialog({
            confirmationTitle: 'Please confirm!',
            confirmationSubTitle: `Are you sure you want to ${item?.isActive ? "deactivate" : "activate"} this facility?`,
            disableEscKeyClose: false,
            disableBackdropClose: true,
            imagePath: `${ImageConfig.ConfirmationImage}`,
            direction: "up",
            yes: {
                text: `${item?.isActive ? "Deactivate" : "Activate"}`,
                color: "primary"
            },
            no: {
                text: "Cancel",
                color: "primary"
            }
        })
            .then(() => {
                onToggleFacility(item);
            })
            .catch(() => {
            })
    }, [onToggleFacility]);


    return (
        <div className="table-list-container">
            <TableWrapperV2Component
                isPaginated={true}
                columns={columns}
                url={APIConfig.FACILITY_LIST.URL}
                method={APIConfig.FACILITY_LIST.METHOD}
                extraPayload={filterData}
                isColumnFilterVisible={true}
                moduleName={MODULE_NAME}
            />
        </div>
    )
};

export default FacilityListTableComponent;
